import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search, Plus, Loader2, RefreshCw, Pencil, Trash2, 
  Code, BarChart3, Eye, EyeOff, ChevronLeft, ChevronRight,
  Copy, Check, FileCode, Zap
} from 'lucide-react';

const API_BASE = window.location.origin || '';

// Script type display info
const scriptTypes = {
  ga4: { label: 'Google Analytics 4', color: 'bg-orange-500/20 text-orange-400', icon: '📊' },
  gtm: { label: 'Google Tag Manager', color: 'bg-blue-500/20 text-blue-400', icon: '🏷️' },
  google_ads: { label: 'Google Ads', color: 'bg-green-500/20 text-green-400', icon: '📈' },
  meta: { label: 'Meta / Facebook', color: 'bg-indigo-500/20 text-indigo-400', icon: '📘' },
  tiktok: { label: 'TikTok Pixel', color: 'bg-pink-500/20 text-pink-400', icon: '🎵' },
  snapchat: { label: 'Snapchat Pixel', color: 'bg-yellow-500/20 text-yellow-400', icon: '👻' },
  hotjar: { label: 'Hotjar', color: 'bg-red-500/20 text-red-400', icon: '🔥' },
  clarity: { label: 'Microsoft Clarity', color: 'bg-cyan-500/20 text-cyan-400', icon: '🔍' },
  custom: { label: 'Custom Script', color: 'bg-gray-500/20 text-gray-400', icon: '⚙️' },
};

const placementOptions = [
  { value: 'HEAD', label: 'Head (<head>)', description: 'Before </head> tag' },
  { value: 'BODY_START', label: 'Body Start', description: 'After <body> tag' },
  { value: 'BODY_END', label: 'Body End', description: 'Before </body> tag' },
];

const pageScopeOptions = [
  { value: 'all', label: 'All Pages', description: 'Load on every page' },
  { value: 'homepage', label: 'Homepage Only', description: 'Only on /' },
  { value: 'booking', label: 'Booking Flow', description: 'Quote, extras, details, payment' },
  { value: 'confirmation', label: 'Confirmation', description: 'Thank you / confirmation pages' },
  { value: 'cms', label: 'CMS Pages', description: 'Dynamic content pages' },
  { value: 'admin', label: 'Admin Panel', description: 'Admin pages only' },
  { value: 'custom', label: 'Custom Rule', description: 'Regex or path pattern' },
];

const AdminTrackingPage = () => {
  const navigate = useNavigate();
  const [scripts, setScripts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [templates, setTemplates] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'ga4',
    status: true,
    placement: 'HEAD',
    script_code: '',
    page_scope: 'all',
    page_rule: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchScripts = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (typeFilter && typeFilter !== 'all') params.append('type', typeFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(`${API_BASE}/api/tracking/admin/list?${params}`, {
        headers: getAuthHeader(),
      });
      
      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setScripts(data.scripts || []);
        setPagination(data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      }
    } catch (error) {
      console.error('Fetch scripts error:', error);
      toast.error('Failed to load tracking scripts');
    } finally {
      setIsLoading(false);
    }
  }, [navigate, searchTerm, typeFilter, statusFilter, pagination.limit]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/tracking/admin/templates`, {
        headers: getAuthHeader(),
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates || {});
      }
    } catch (error) {
      console.error('Fetch templates error:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchScripts();
    fetchTemplates();
  }, [navigate, fetchScripts]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchScripts(1);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'ga4',
      status: true,
      placement: 'HEAD',
      script_code: '',
      page_scope: 'all',
      page_rule: '',
    });
    setIsEditing(false);
    setSelectedScript(null);
  };

  const openEditDialog = (script) => {
    setSelectedScript(script);
    setIsEditing(true);
    setFormData({
      name: script.name || '',
      type: script.type || 'custom',
      status: script.status ?? true,
      placement: script.placement || 'HEAD',
      script_code: script.script_code || '',
      page_scope: script.page_scope || 'all',
      page_rule: script.page_rule || '',
    });
    setShowCreateDialog(true);
  };

  const applyTemplate = (templateKey) => {
    const template = templates[templateKey];
    if (template) {
      setFormData({
        ...formData,
        name: template.name,
        type: template.type,
        placement: template.placement,
        script_code: template.template,
      });
      setShowTemplatesDialog(false);
      setShowCreateDialog(true);
      toast.info(`Template loaded: ${template.name}. ${template.placeholder}`);
    }
  };

  const copyTemplate = (templateKey) => {
    const template = templates[templateKey];
    if (template) {
      navigator.clipboard.writeText(template.template);
      setCopiedTemplate(templateKey);
      setTimeout(() => setCopiedTemplate(null), 2000);
      toast.success('Template copied to clipboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        status: formData.status,
        placement: formData.placement,
        script_code: formData.script_code,
        page_scope: formData.page_scope,
        page_rule: formData.page_scope === 'custom' ? formData.page_rule : null,
      };
      
      const url = isEditing 
        ? `${API_BASE}/api/tracking/admin/${selectedScript.id}`
        : `${API_BASE}/api/tracking/admin/create`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(isEditing ? 'Script updated' : 'Script created');
        setShowCreateDialog(false);
        resetForm();
        fetchScripts(pagination.page);
      } else {
        toast.error(data.detail || 'Failed to save script');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to save script');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = async (script) => {
    try {
      const response = await fetch(`${API_BASE}/api/tracking/admin/${script.id}/toggle`, {
        method: 'POST',
        headers: getAuthHeader(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        fetchScripts(pagination.page);
      } else {
        toast.error(data.detail || 'Failed to toggle script');
      }
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to toggle script');
    }
  };

  const handleDelete = async (script) => {
    if (!window.confirm(`Are you sure you want to delete "${script.name}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/tracking/admin/${script.id}`, {
        method: 'DELETE',
        headers: getAuthHeader(),
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Script deleted');
        fetchScripts(pagination.page);
      } else {
        toast.error(data.detail || 'Failed to delete script');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete script');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="tracking-title">
                Tracking & Pixels
              </h1>
              <p className="text-muted-foreground">Manage analytics, tracking, and marketing pixels</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowTemplatesDialog(true)}
                data-testid="templates-btn"
              >
                <FileCode className="h-4 w-4 mr-2" />
                Templates
              </Button>
              <Button 
                onClick={() => { resetForm(); setShowCreateDialog(true); }}
                data-testid="create-script-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Script
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="bg-primary/5 border-primary/20 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm text-foreground font-medium">Scripts are injected automatically</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Active scripts are loaded on every page based on their placement and scope settings. 
                    Changes take effect immediately without deployment.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="bg-secondary border-border mb-6">
            <CardContent className="p-4">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]" data-testid="type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(scriptTypes).map(([key, info]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span>{info.icon}</span>
                          <span>{info.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[140px]" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" variant="outline" data-testid="filter-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Scripts Table */}
          <Card className="bg-secondary border-border">
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : scripts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Code className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No tracking scripts configured</p>
                  <Button 
                    variant="link" 
                    onClick={() => setShowTemplatesDialog(true)}
                    className="mt-2"
                  >
                    Start with a template
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Name</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Type</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Placement</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Scope</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Updated</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scripts.map((script) => {
                        const typeInfo = scriptTypes[script.type] || scriptTypes.custom;
                        return (
                          <tr 
                            key={script.id} 
                            className="border-b border-border hover:bg-muted/30 transition-colors"
                            data-testid={`script-row-${script.id}`}
                          >
                            <td className="p-4">
                              <Switch
                                checked={script.status}
                                onCheckedChange={() => handleToggle(script)}
                                data-testid={`toggle-${script.id}`}
                              />
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {script.status ? (
                                  <Eye className="h-4 w-4 text-green-500" />
                                ) : (
                                  <EyeOff className="h-4 w-4 text-gray-500" />
                                )}
                                <span className="text-foreground font-medium">{script.name}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs ${typeInfo.color}`}>
                                {typeInfo.icon} {typeInfo.label}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {script.placement}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {pageScopeOptions.find(p => p.value === script.page_scope)?.label || script.page_scope}
                              {script.page_rule && (
                                <span className="block text-xs text-primary">{script.page_rule}</span>
                              )}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {formatDate(script.updated_at)}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(script)}
                                  title="Edit"
                                  data-testid={`edit-${script.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(script)}
                                  title="Delete"
                                  className="text-red-500 hover:text-red-400"
                                  data-testid={`delete-${script.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchScripts(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchScripts(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Tracking Script' : 'Add Tracking Script'}</DialogTitle>
            <DialogDescription>
              {isEditing ? 'Update script configuration' : 'Add a new analytics or tracking script'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Google Analytics"
                  required
                  data-testid="script-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger data-testid="script-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(scriptTypes).map(([key, info]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span>{info.icon}</span>
                          <span>{info.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Placement & Scope */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="placement">Placement *</Label>
                <Select 
                  value={formData.placement} 
                  onValueChange={(v) => setFormData({ ...formData, placement: v })}
                >
                  <SelectTrigger data-testid="script-placement-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {placementOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div>
                          <div>{opt.label}</div>
                          <div className="text-xs text-muted-foreground">{opt.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="page_scope">Page Scope *</Label>
                <Select 
                  value={formData.page_scope} 
                  onValueChange={(v) => setFormData({ ...formData, page_scope: v })}
                >
                  <SelectTrigger data-testid="script-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pageScopeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div>
                          <div>{opt.label}</div>
                          <div className="text-xs text-muted-foreground">{opt.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Custom Rule */}
            {formData.page_scope === 'custom' && (
              <div className="space-y-2">
                <Label htmlFor="page_rule">Custom Rule (Path or Regex)</Label>
                <Input
                  id="page_rule"
                  value={formData.page_rule}
                  onChange={(e) => setFormData({ ...formData, page_rule: e.target.value })}
                  placeholder="/booking or ^/products/.*"
                />
                <p className="text-xs text-muted-foreground">
                  Enter a path prefix (e.g., /booking) or regex pattern (e.g., ^/products/.*)
                </p>
              </div>
            )}

            {/* Script Code */}
            <div className="space-y-2">
              <Label htmlFor="script_code">Script Code *</Label>
              <Textarea
                id="script_code"
                value={formData.script_code}
                onChange={(e) => setFormData({ ...formData, script_code: e.target.value })}
                placeholder="<script>...</script>"
                rows={10}
                className="font-mono text-sm"
                required
                data-testid="script-code-input"
              />
              <p className="text-xs text-muted-foreground">
                Paste the full tracking script including &lt;script&gt; and &lt;noscript&gt; tags
              </p>
            </div>

            {/* Status */}
            <div className="flex items-center space-x-2">
              <Switch
                id="status"
                checked={formData.status}
                onCheckedChange={(c) => setFormData({ ...formData, status: c })}
              />
              <Label htmlFor="status">Active (script will be injected on pages)</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="save-script-btn">
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog open={showTemplatesDialog} onOpenChange={setShowTemplatesDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Script Templates</DialogTitle>
            <DialogDescription>
              Start with a pre-configured template for popular tracking services
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {Object.entries(templates).map(([key, template]) => {
              const typeInfo = scriptTypes[template.type] || scriptTypes.custom;
              return (
                <Card key={key} className="bg-muted/30 border-border hover:bg-muted/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-medium text-foreground flex items-center gap-2">
                          <span>{typeInfo.icon}</span>
                          {template.name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Placement: {template.placement}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${typeInfo.color}`}>
                        {template.type}
                      </span>
                    </div>
                    <p className="text-xs text-primary mb-3">{template.placeholder}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => applyTemplate(key)}
                        className="flex-1"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Use Template
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyTemplate(key)}
                      >
                        {copiedTemplate === key ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplatesDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrackingPage;
